/**
 * Shared 52px top app bar + primary navigation, used identically on all screens.
 * One component keeps the bar consistent across route changes.
 *
 * Layout (design handoff, mockup_2 top bar):
 *   Left:  logo square (ЕР) + "ЕДРОшка" + region mono tag (from session).
 *   Mid:   nav tabs (Активисты · Обновление · Задачи).
 *   Right: a page-specific actions slot (`children`), a 260px search field,
 *          an avatar, and the LogoutButton.
 */

import * as React from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";

import type { FilterClause } from "@/api/client";
import { ActiveJobsIndicator } from "@/components/jobs/ActiveJobsIndicator";
import { LogoutButton } from "@/components/LogoutButton";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useSession } from "@/api/hooks";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useFilters } from "@/store/filters";
import { cn } from "@/lib/utils";

/**
 * Region code (from the login domain, e.g. `…@tyumen.er.ru` → `"tyumen"`) →
 * human-readable label. Unknown codes fall back to `code.toUpperCase()`.
 */
const REGION_LABELS: Record<string, string> = {
  tyumen: "Тюмень",
  yanao: "ЯНАО",
  hmao: "ХМАО",
  tatarstan: "Татарстан",
  bashkortostan: "Башкортостан",
  moscow: "Москва",
  spb: "Санкт-Петербург",
  ekb: "Екатеринбург",
  novosibirsk: "Новосибирск",
};

/** Display label for a region code, or null when there's nothing to show. */
function regionLabel(region: string | null | undefined): string | null {
  if (!region) return null;
  const key = region.trim().toLowerCase();
  if (key === "") return null;
  return REGION_LABELS[key] ?? region.toUpperCase();
}

interface NavItem {
  to: string;
  label: string;
  /** `match` decides which tab lights up for the current pathname. */
  match: (p: string) => boolean;
}

const navItems: NavItem[] = [
  { to: "/", label: "Активисты", match: (p) => p === "/" || p.startsWith("/activists") },
  { to: "/import", label: "Обновление", match: (p) => p.startsWith("/import") },
  { to: "/scheduled", label: "Задачи", match: (p) => p.startsWith("/scheduled") },
];

/** Shown only to super-admins (whoami.is_admin). Appended to the nav at render time. */
const adminNavItem: NavItem = {
  to: "/admin",
  label: "Админ",
  match: (p) => p.startsWith("/admin"),
};

/**
 * Topbar quick-search → Base filter.
 *
 * The search owns EXACTLY ONE clause — the last one it applied (tracked in a ref).
 * A digit-only query targets `phone_mobile` (contains), any other text `last_name`
 * (contains). On each change we remove only OUR previous clause and add the new one,
 * leaving every other filter — including name/phone clauses set elsewhere (facets,
 * saved filters, URL hydration) — untouched. This is why external edits no longer
 * clobber the search and the search no longer clobbers them (Xfe-6). fields.ts has
 * no combined full-name field, so `last_name` is the closest text target.
 */
const BASE_PATH = "/";

/** Value-equality for two (nullable) filter clauses. */
function sameClause(a: FilterClause | null, b: FilterClause | null): boolean {
  if (a === null || b === null) return a === b;
  return a.field === b.field && a.operator === b.operator && String(a.value) === String(b.value);
}

export function AppHeader({ children }: { children?: React.ReactNode }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const filters = useFilters((s) => s.filters);
  const setFilters = useFilters((s) => s.setFilters);
  const session = useSession();
  const region = regionLabel(session.data?.region);

  const [query, setQuery] = React.useState("");
  const debounced = useDebouncedValue(query, 350);
  // The one clause the search currently owns (what it last pushed into the store).
  const appliedRef = React.useRef<FilterClause | null>(null);

  // Build the search clause for the (debounced) query: digits → phone, else last_name.
  const clauseFor = React.useCallback((raw: string): FilterClause | null => {
    const q = raw.trim();
    if (q === "") return null;
    const field = /^\d+$/.test(q) ? "phone_mobile" : "last_name";
    return { field, operator: "contains", value: q };
  }, []);

  // Fires only when the debounced query changes. We remove ONLY our previously
  // applied clause and add the new one, so external filter edits (facets, saved
  // filters, URL hydration, chip removal) are never clobbered — and an empty query
  // on mount is a no-op instead of stripping an externally-set name/phone filter.
  React.useEffect(() => {
    const clause = clauseFor(debounced);
    if (sameClause(clause, appliedRef.current)) return;
    const current = useFilters.getState().filters;
    const prev = appliedRef.current;
    const kept = prev ? current.filter((c) => !sameClause(c, prev)) : current.slice();
    const next = clause ? [...kept, clause] : kept;
    appliedRef.current = clause;
    if (next.length === current.length && JSON.stringify(next) === JSON.stringify(current)) {
      return;
    }
    if (clause && pathname !== BASE_PATH) navigate(BASE_PATH);
    setFilters(next);
    // pathname/navigate intentionally omitted: re-running on route change would
    // re-apply the search clause and fight URL-driven filter hydration.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced, clauseFor, setFilters]);

  // If OUR clause is removed externally (e.g. "Сбросить" or chip removal), drop the
  // reference and clear the box — but leave the box alone for unrelated filter edits.
  React.useEffect(() => {
    const prev = appliedRef.current;
    if (prev && !filters.some((c) => sameClause(c, prev))) {
      appliedRef.current = null;
      if (query !== "") setQuery("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  return (
    <header
      className="flex h-[52px] shrink-0 items-center gap-5 border-b bg-[var(--panel)] px-5"
      style={{ borderColor: "var(--border-hex)" }}
    >
      {/* Logo */}
      <div className="flex items-center gap-[9px]">
        <div className="flex h-[26px] w-[26px] items-center justify-center rounded-[7px] bg-[var(--accent-hex)] text-[12px] font-bold tracking-[-0.04em] text-white">
          ЕР
        </div>
        <span className="text-[14px] font-bold tracking-[0.02em]">ЕДРОшка</span>
        {region && (
          <span className="ml-0.5 font-mono text-[11px] text-[var(--text-muted)]">{region}</span>
        )}
      </div>

      {/* Nav tabs (admin tab appended only for super-admins) */}
      <nav className="flex items-center gap-[3px] text-[13px]">
        {(session.data?.is_admin ? [...navItems, adminNavItem] : navItems).map((item) => {
          const active = item.match(pathname);
          return (
            <NavLink
              key={item.to}
              to={item.to}
              className={cn(
                "rounded-[8px] px-3 py-[6px] transition-colors",
                active
                  ? "bg-[var(--accent-weak)] font-semibold text-[var(--accent-weak-text)]"
                  : "font-medium text-[var(--icon-muted)] hover:text-foreground",
              )}
            >
              {item.label}
            </NavLink>
          );
        })}
      </nav>

      {/* Right cluster */}
      <div className="ml-auto flex items-center gap-3">
        {children}
        <div className="flex h-[34px] w-[260px] items-center gap-2 rounded-[9px] border bg-[var(--app-bg)] px-3" style={{ borderColor: "var(--border-hex)" }}>
          <Search className="h-[14px] w-[14px] text-[var(--text-muted)]" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Поиск по ФИО, телефону…"
            className="w-full bg-transparent text-[13px] text-foreground placeholder:text-[var(--text-muted)] focus:outline-none"
          />
          {query !== "" && (
            <button
              type="button"
              onClick={() => setQuery("")}
              aria-label="Очистить поиск"
              className="shrink-0 text-[var(--text-muted)] hover:text-foreground"
            >
              <X className="h-[14px] w-[14px]" />
            </button>
          )}
        </div>
        <ActiveJobsIndicator />
        <ThemeToggle />
        <div className="flex h-[28px] w-[28px] items-center justify-center rounded-full bg-[var(--accent-avatar)] text-[11px] font-semibold text-[var(--accent-hex)]">
          КА
        </div>
        <LogoutButton />
      </div>
    </header>
  );
}
